import { z } from 'zod'

// Phone validation schema
export const PhoneSchema = z.object({
  value: z.string().min(1, 'Phone number is required'),
  formatted: z.string(),
  rawValue: z.string().regex(/^\d{10,14}$/, 'Please enter a valid 10-digit phone number'),
  country: z.string().optional(),
})

// Email validation schema
export const EmailSchema = z.object({
  value: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
})

// Address validation schemas
export const AddressFieldSchema = z.object({
  street: z.string().min(3, 'Street address must be at least 3 characters'),
  apartment: z.string().optional(),
  city: z.string().min(2, 'City must be at least 2 characters'),
  state: z.string().length(2, 'Please use 2-letter state code (e.g., CA)'),
  zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Please enter a valid ZIP code'),
  country: z.string().default('US'),
})

// Payment validation schemas
export const PaymentFieldSchema = z.object({
  cardNumber: z.string().min(13).max(19),
  expiryMonth: z.string().regex(/^(0?[1-9]|1[0-2])$/, 'Invalid month'),
  expiryYear: z.string().regex(/^\d{4}$/, 'Invalid year'),
  cvv: z.string().min(3).max(4).regex(/^\d+$/, 'Invalid CVV'),
  holderName: z.string().min(2, 'Cardholder name is required').optional(),
})

// Form data schemas
export const SignInFormSchema = z.object({
  email: EmailSchema.shape.value.optional(),
  phone: z.string().optional(),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
})

export const SignUpFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: EmailSchema.shape.value,
  phone: z.string().optional(),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

export const AddressFormSchema = z.object({
  label: z.string().optional(),
  street: AddressFieldSchema.shape.street,
  apartment: AddressFieldSchema.shape.apartment,
  city: AddressFieldSchema.shape.city,
  state: AddressFieldSchema.shape.state,
  zipCode: AddressFieldSchema.shape.zipCode,
  instructions: z.string().optional(),
})

export const PaymentFormSchema = z.object({
  cardNumber: PaymentFieldSchema.shape.cardNumber,
  expiryMonth: PaymentFieldSchema.shape.expiryMonth,
  expiryYear: PaymentFieldSchema.shape.expiryYear,
  cvv: PaymentFieldSchema.shape.cvv,
  holderName: PaymentFieldSchema.shape.holderName,
  billingAddress: AddressFormSchema.optional(),
})

// Validation preferences schema
export const ValidationPreferencesSchema = z.object({
  enableRealTimeValidation: z.boolean().default(true),
  phoneFormat: z.enum(['us', 'international']).default('us'),
  showValidationIcons: z.boolean().default(true),
  validationDelay: z.number().min(0).max(2000).default(300),
})

// Export types
export type PhoneValidation = z.infer<typeof PhoneSchema>
export type EmailValidation = z.infer<typeof EmailSchema>
export type AddressFields = z.infer<typeof AddressFieldSchema>
export type PaymentFields = z.infer<typeof PaymentFieldSchema>
export type SignInForm = z.infer<typeof SignInFormSchema>
export type SignUpForm = z.infer<typeof SignUpFormSchema>
export type AddressForm = z.infer<typeof AddressFormSchema>
export type PaymentForm = z.infer<typeof PaymentFormSchema>
export type ValidationPreferences = z.infer<typeof ValidationPreferencesSchema>

// Field validation result type
export interface FieldValidation<T = string> {
  value: T
  isValid: boolean
  error: string | null
  touched: boolean
}

// Enhanced field validation for specific types
export interface PhoneFieldValidation extends FieldValidation {
  formatted: string
  rawValue: string
  country?: string
}

export interface PaymentCardValidation extends FieldValidation {
  brand?: string
  last4?: string
}

export interface ValidationSummary {
  totalFields: number
  validFields: number
  invalidFields: number
  errors: Array<{ field: string; message: string }>
  isValid: boolean
}

// Form validation types
export interface FormValidation {
  hasErrors: boolean
  touchedFields: string[]
  isFormValid: boolean
  errors: Record<string, string>
}